import { apiSlice } from '../api/apiSlice'
import {
  INTEREST_API_PATH,
  INTEREST_CACHE_TAG,
  INTEREST_PAGE_SIZE,
} from './interests.constant'
import { isInterestListEnvelope } from './interests.helpers'
import { BaseApiResponse } from '../shared/shared.type'
import type {
  CreateInterestPayload,
  InterestMutationData,
  ReorderInterestsPayload,
  ShoppingInterest,
  UpdateInterestRequest,
} from './interests.type'

export const interestsApiSlice = apiSlice.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getAllInterests: builder.query<ShoppingInterest[], null>({
      /**
       * Loads the complete catalogue because the reorder endpoint requires
       * every non-archived ID, not only one visible page.
       */
      async queryFn(_argument, _api, _extraOptions, fetchWithBaseQuery) {
        const interests: ShoppingInterest[] = []
        let skip = 0
        let hasMorePages = true

        while (hasMorePages) {
          const response = await fetchWithBaseQuery({
            url: INTEREST_API_PATH,
            params: { skip, limit: INTEREST_PAGE_SIZE },
          })
          if (response.error !== undefined) return { error: response.error }

          const responseData: unknown = response.data
          if (!isInterestListEnvelope(responseData)) {
            return {
              error: {
                status: 'PARSING_ERROR',
                originalStatus: 200,
                data: responseData,
                error: 'Invalid shopping interest response',
              },
            }
          }

          const pageInterests = responseData.data.interests
          interests.push(...pageInterests)
          hasMorePages =
            pageInterests.length === INTEREST_PAGE_SIZE &&
            interests.length < responseData.data.count
          if (hasMorePages) {
            skip += pageInterests.length
          }
        }

        return { data: interests }
      },
      providesTags: [{ type: 'INTEREST', id: INTEREST_CACHE_TAG.LIST }],
    }),
    createInterest: builder.mutation<
      BaseApiResponse<InterestMutationData>,
      CreateInterestPayload
    >({
      query: (body) => ({
        url: INTEREST_API_PATH,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'INTEREST', id: INTEREST_CACHE_TAG.LIST }],
    }),
    updateInterest: builder.mutation<
      BaseApiResponse<InterestMutationData>,
      UpdateInterestRequest
    >({
      query: ({ interestId, body }) => ({
        url: `${INTEREST_API_PATH}/${interestId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'INTEREST', id: arg.interestId },
        { type: 'INTEREST', id: INTEREST_CACHE_TAG.LIST },
      ],
    }),
    reorderInterests: builder.mutation<
      BaseApiResponse<unknown>,
      ReorderInterestsPayload
    >({
      query: (body) => ({
        url: `${INTEREST_API_PATH}/order`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'INTEREST', id: INTEREST_CACHE_TAG.LIST }],
    }),
  }),
})

export const {
  useCreateInterestMutation,
  useGetAllInterestsQuery,
  useReorderInterestsMutation,
  useUpdateInterestMutation,
} = interestsApiSlice
